import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LogOut, LayoutDashboard, Shield, ChevronDown, Users, Trophy, GraduationCap, Info, Building2, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

const JPA_LOGO = "/logos/jpa-logo.png";

const PLAYERS_ITEMS = [
  { to: "/players", label: "Players", icon: Users, desc: "All registered JPA athletes" },
  { to: "/rankings", label: "Rankings", icon: Trophy, desc: "By format, age & gender" },
  { to: "/coaches", label: "Coaches", icon: GraduationCap, desc: "Certified state coaches" },
];
const ABOUT_ITEMS = [
  { to: "/about", label: "About JPA", icon: Info, desc: "History, team & AIPA affiliation" },
  { to: "/sponsors", label: "Sponsors", icon: Building2, desc: "Prime, partners & suppliers" },
  { to: "/contact", label: "Contact", icon: Phone, desc: "Reach the JPA office" },
];

const TOP_NAV = [
  { to: "/", label: "Home" },
  { to: "/tournaments", label: "Tournaments" },
  { key: "players", label: "Players", dropdown: PLAYERS_ITEMS },
  { to: "/clubs", label: "Clubs" },
  { to: "/videos", label: "Videos" },
  { to: "/news", label: "News" },
  { key: "about", label: "About", dropdown: ABOUT_ITEMS },
];

function NavDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = items.some((i) => location.pathname === i.to);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testId}
        className={`flex items-center gap-1 px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-full transition-colors ${
          active ? "text-jpa-primary" : "text-jpa-ink hover:text-jpa-primary"
        }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50 min-w-[280px]">
          <div className="bg-white border border-jpa-border rounded-sm shadow-xl overflow-hidden">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`${testId}-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flex items-start gap-3 p-4 hover:bg-jpa-primary hover:text-white transition-colors group"
              >
                <it.icon className="w-5 h-5 mt-0.5 text-jpa-primary group-hover:text-jpa-accent transition-colors" />
                <div>
                  <div className="font-bold uppercase text-sm tracking-widest">{it.label}</div>
                  <div className="text-xs text-jpa-muted group-hover:text-white/80 transition-colors">{it.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      {/* Top strip */}
      <div className="bg-jpa-ink text-white text-xs">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex justify-between items-center py-1.5 font-medium">
          <span className="tracking-wider uppercase">Jharkhand Pickleball Association • Official AIPA affiliate</span>
          <span className="hidden md:inline tracking-wider">📞 +91 93047 57995 • info@jpaofficial.com</span>
        </div>
      </div>

      {/* Floating pill ribbon */}
      <div className={`transition-all duration-300 ${scrolled ? "py-2" : "py-4"}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className={`backdrop-blur-xl border border-jpa-border rounded-full shadow-lg flex items-center justify-between transition-all ${scrolled ? "bg-white/95 py-2 px-3" : "bg-white/95 py-2.5 px-4"}`}>
            <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group pl-2">
              <div className="bg-white rounded-full p-0.5 border border-jpa-border">
                <img src={JPA_LOGO} alt="JPA" className="h-10 md:h-12 w-10 md:w-12 object-contain" />
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="font-display text-xl md:text-2xl tracking-wider text-jpa-ink">JPA</div>
                <div className="text-[9px] text-jpa-muted uppercase tracking-widest -mt-1">Jharkhand Pickleball</div>
              </div>
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              {TOP_NAV.map((item) =>
                item.dropdown ? (
                  <NavDropdown key={item.key} label={item.label} items={item.dropdown} testId={`nav-${item.key}`} />
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-full transition-colors ${
                      location.pathname === item.to ? "text-jpa-primary" : "text-jpa-ink hover:text-jpa-primary"
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            <div className="flex items-center gap-2">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-jpa-surface transition-colors">
                      <div className="w-8 h-8 rounded-full bg-jpa-primary text-white grid place-items-center text-xs font-bold uppercase">
                        {user.name?.[0] || "P"}
                      </div>
                      <span className="hidden md:inline text-sm font-semibold">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild data-testid="menu-dashboard">
                      <Link to={user.role === "club" ? "/club/dashboard" : "/dashboard"}><LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard</Link>
                    </DropdownMenuItem>
                    {user.role === "club" && (
                      <DropdownMenuItem asChild data-testid="menu-club-dashboard">
                        <Link to="/club/dashboard"><Building2 className="w-4 h-4 mr-2" /> Club Dashboard</Link>
                      </DropdownMenuItem>
                    )}
                    {user.role === "admin" && (
                      <DropdownMenuItem asChild data-testid="menu-admin">
                        <Link to="/admin"><Shield className="w-4 h-4 mr-2" /> Admin panel</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> Log out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" data-testid="header-login" className="hidden md:inline text-sm font-bold uppercase tracking-widest text-jpa-ink hover:text-jpa-primary px-3 py-2">
                    Log In
                  </Link>
                  <Link to="/tournaments" data-testid="header-register">
                    <Button className="bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-full px-5">
                      Register
                    </Button>
                  </Link>
                </>
              )}
              <button
                className="xl:hidden ml-1 p-2 rounded-full hover:bg-jpa-surface"
                onClick={() => setOpen((o) => !o)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-jpa-border">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
            <div className="grid grid-cols-2 gap-1">
              {[
                ["Home", "/"],
                ["Tournaments", "/tournaments"],
                ["Players", "/players"],
                ["Rankings", "/rankings"],
                ["Coaches", "/coaches"],
                ["Clubs", "/clubs"],
                ["Videos", "/videos"],
                ["News", "/news"],
                ["About JPA", "/about"],
                ["Sponsors", "/sponsors"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="px-3 py-2 text-sm font-bold uppercase tracking-wider text-jpa-ink hover:text-jpa-primary"
                >
                  {label}
                </Link>
              ))}
            </div>
            {/* Auth links on mobile */}
            {!user ? (
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-jpa-border pt-4">
                <Link to="/login" data-testid="mobile-login" className="text-center py-2 border border-jpa-border rounded-sm text-sm font-bold uppercase tracking-widest text-jpa-ink hover:border-jpa-primary hover:text-jpa-primary">
                  Log in
                </Link>
                <Link to="/tournaments" data-testid="mobile-register" className="text-center py-2 bg-jpa-primary text-white rounded-sm text-sm font-bold uppercase tracking-widest hover:bg-jpa-primary-hover">
                  Register
                </Link>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-jpa-border pt-4">
                <Link to="/profile" data-testid="mobile-profile" className="text-center py-2 border border-jpa-border rounded-sm text-sm font-bold uppercase tracking-widest text-jpa-ink hover:border-jpa-primary hover:text-jpa-primary">
                  My profile
                </Link>
                <button onClick={logout} data-testid="mobile-logout" className="text-center py-2 border border-jpa-border rounded-sm text-sm font-bold uppercase tracking-widest text-jpa-ink hover:border-jpa-live hover:text-jpa-live">
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
